import { CheckCircledIcon, ReloadIcon, RocketIcon } from "@radix-ui/react-icons";
import { Box, Button, Flex, IconButton, Text, Tooltip } from "@radix-ui/themes";
import { type FormEvent, useEffect, useState } from "react";
import type { UpgradeSurface } from "../../../../utils/conversion-shared";
import type { ConversionContext } from "../../contexts/UsageContext";
import { isCheckoutSignal } from "../../utils/checkoutSignal";
import { trackUpgradeClicked } from "../../utils/conversionAnalytics";
import { readSessionCache, writeSessionCache } from "../../utils/sessionCache";
import { formatPeriodEnd } from "../../utils/usageMonths";

export type BillingAccessSummary = {
    hasProAccess: boolean;
    cancelable: boolean;
    resumable: boolean;
    status: "active" | "canceled" | "incomplete" | "incomplete_expired" | "past_due" | "paused" | "trialing" | "unpaid" | "inactive" | "unavailable";
    renews: boolean | null;
    currentPeriodEnd: string | null;
};

type BillingResponse = {
    subscription?: BillingAccessSummary;
    error_description?: string;
};

type SubscriptionCardProps = {
    checkoutUrl: string;
    subscriptionUrl: string;
    upgradeSurface: UpgradeSurface;
    conversionContext: ConversionContext | null;
    /**
     * True when some OTHER "Upgrade to Pro" button on the page (e.g. the
     * sidebar widget) already started a checkout. A page can show more than
     * one such button at once, and disabling only the one that was clicked
     * would leave this one live for a second checkout session.
     */
    externallyLocked?: boolean;
    /** Fires whenever this card's own checkout-in-progress state changes, so a parent can share it with sibling buttons via externallyLocked. */
    onCheckoutStateChange?: (started: boolean) => void;
    /** Fires on every subscription read, so a parent can keep other "is Pro" surfaces (usage plan, badges) in sync instead of them going stale. */
    onSubscriptionChange?: (subscription: BillingAccessSummary) => void;
    /**
     * Runs first on an upgrade submit. Returning true holds the checkout (the account check dialog),
     * which then replays the submit when the user continues.
     */
    onBeforeCheckout?: (event: FormEvent<HTMLFormElement>) => boolean;
};

const subscriptionStatusLabels: Record<BillingAccessSummary["status"], string> = {
    active: "Active",
    canceled: "Canceled",
    incomplete: "Incomplete",
    incomplete_expired: "Expired",
    past_due: "Payment past due",
    paused: "Paused",
    trialing: "Trial",
    unpaid: "Unpaid",
    inactive: "Inactive",
    unavailable: "Unavailable",
};

const SubscriptionCard = ({
    checkoutUrl,
    subscriptionUrl,
    upgradeSurface,
    conversionContext,
    externallyLocked,
    onCheckoutStateChange,
    onSubscriptionChange,
    onBeforeCheckout,
}: SubscriptionCardProps) => {
    // Per-tab cache shows the last known status immediately while the request
    // below revalidates it in the background.
    const [subscription, setSubscription] = useState<BillingAccessSummary | null>(() => readSessionCache<BillingAccessSummary>(subscriptionUrl));
    const [billingError, setBillingError] = useState("");
    const [checkoutStarted, setCheckoutStarted] = useState(false);
    const [subscriptionRefresh, setSubscriptionRefresh] = useState(0);

    // Notify the parent only from real local transitions. An effect-based
    // mount guard is unsafe under React StrictMode's development effect replay:
    // its second setup can publish the initial false state and clear a sibling's
    // active checkout lock.
    const updateCheckoutStarted = (started: boolean) => {
        setCheckoutStarted(started);
        onCheckoutStateChange?.(started);
    };

    useEffect(() => {
        if (subscription) {
            onSubscriptionChange?.(subscription);
        }
    }, [
        subscription,
    ]);

    const refreshSubscriptionStatus = () => {
        updateCheckoutStarted(false);
        setSubscription(null);
        setBillingError("");
        setSubscriptionRefresh((refresh) => refresh + 1);
    };

    // Checkout attempts are replaceable. Reset this transient opening state as
    // soon as the user returns to the dashboard, or shortly after submission if
    // the popup was blocked. A Stripe tab must never impose a multi-minute lock.
    useEffect(() => {
        if (!checkoutStarted) {
            return;
        }
        const handleStorage = (event: StorageEvent) => {
            if (isCheckoutSignal(event)) {
                refreshSubscriptionStatus();
            }
        };
        window.addEventListener("storage", handleStorage);
        window.addEventListener("focus", refreshSubscriptionStatus);
        const timeoutId = window.setTimeout(refreshSubscriptionStatus, 5 * 1000);
        return () => {
            window.removeEventListener("storage", handleStorage);
            window.removeEventListener("focus", refreshSubscriptionStatus);
            window.clearTimeout(timeoutId);
        };
    }, [
        checkoutStarted,
    ]);

    useEffect(() => {
        let controller: AbortController | undefined;
        let retryTimer: number | undefined;
        const loadSubscription = (retriesRemaining = 0) => {
            controller?.abort();
            window.clearTimeout(retryTimer);
            const requestController = new AbortController();
            controller = requestController;
            setBillingError("");

            void fetch(subscriptionUrl, {
                credentials: "include",
                signal: requestController.signal,
            })
                .then(async (response) => {
                    const body = (await response.json()) as BillingResponse;
                    if (!response.ok || !body.subscription) {
                        throw new Error(body.error_description || "Failed to load subscription");
                    }
                    setSubscription(body.subscription);
                    writeSessionCache(subscriptionUrl, body.subscription);
                    if (!body.subscription.hasProAccess && retriesRemaining > 0) {
                        // ponytail: bounded polling covers delayed webhooks; add push updates only if ten seconds proves insufficient.
                        retryTimer = window.setTimeout(() => loadSubscription(retriesRemaining - 1), 2000);
                    }
                })
                .catch((error) => {
                    if (!requestController.signal.aborted) {
                        setBillingError(error instanceof Error ? error.message : "Failed to load subscription");
                    }
                });
        };

        const refreshAfterCheckout = () => loadSubscription(5);
        refreshAfterCheckout();
        window.addEventListener("focus", refreshAfterCheckout);
        return () => {
            window.removeEventListener("focus", refreshAfterCheckout);
            window.clearTimeout(retryTimer);
            controller?.abort();
        };
    }, [
        subscriptionUrl,
        subscriptionRefresh,
    ]);

    const formattedPeriodEnd = subscription?.currentPeriodEnd ? formatPeriodEnd(subscription.currentPeriodEnd) : null;

    const refreshButton = (
        <Tooltip content="Refresh subscription status">
            <IconButton
                type="button"
                size="2"
                variant="ghost"
                color="gray"
                aria-label="Refresh subscription status"
                onClick={refreshSubscriptionStatus}
                style={{
                    margin: 0,
                }}
            >
                <ReloadIcon aria-hidden="true" />
            </IconButton>
        </Tooltip>
    );

    // Renders chrome-less so it can sit inside the plan card that already provides
    // the name, price and framing — this owns only the status and the actions.
    return (
        <Box>
            {subscription === null ? (
                <Flex direction="column" gap="2">
                    <Text as="p" size="2" color="gray" role={billingError ? "alert" : undefined}>
                        {billingError || "Checking subscription…"}
                    </Text>
                    {billingError && (
                        <Box>
                            <Button type="button" size="2" variant="soft" onClick={refreshSubscriptionStatus}>
                                Retry
                            </Button>
                        </Box>
                    )}
                </Flex>
            ) : subscription.hasProAccess ? (
                <Box>
                    <Flex align="center" gap="2" mb="1">
                        <CheckCircledIcon aria-hidden="true" color="var(--dc-success)" />
                        <Text size="2" weight="medium">
                            Pro access active
                        </Text>
                    </Flex>
                    <Text as="p" size="2" color="gray">
                        Subscription status: {subscriptionStatusLabels[subscription.status]}.
                    </Text>
                    <Text as="p" size="2" color="gray">
                        {subscription.renews === true
                            ? formattedPeriodEnd
                                ? `Renews on ${formattedPeriodEnd}.`
                                : "Automatic renewal is enabled."
                            : subscription.renews === false
                              ? formattedPeriodEnd
                                  ? `Will not renew. Your Pro access remains active until ${formattedPeriodEnd}.`
                                  : "Will not renew. Your Pro access remains active for the rest of the paid period."
                              : "Renewal status is currently unavailable."}
                    </Text>
                    {billingError && (
                        <Text as="p" size="2" color="red" mt="2" role="alert">
                            {billingError}
                        </Text>
                    )}
                </Box>
            ) : checkoutStarted ? (
                // Brief opening state only. Returning from Stripe or the short
                // fallback timer re-arms checkout so an abandoned attempt can be
                // replaced immediately.
                <Flex direction="column" gap="3">
                    <Box>
                        <Text as="div" size="2" weight="medium">
                            Subscription in progress
                        </Text>
                        <Text as="p" size="2" color="gray">
                            Complete payment in the Stripe tab. If you close it, return here and try again.
                        </Text>
                    </Box>
                    <Flex align="center" gap="2">
                        <Button
                            type="button"
                            size="3"
                            disabled
                            style={{
                                flex: 1,
                            }}
                        >
                            Subscription in progress…
                        </Button>
                        {refreshButton}
                    </Flex>
                </Flex>
            ) : externallyLocked ? (
                // A sibling "Upgrade to Pro" button (e.g. the sidebar widget) already
                // started a checkout — the exact same risk this card's own
                // checkoutStarted guards against, just triggered from elsewhere on
                // the page.
                <Button
                    type="button"
                    size="3"
                    disabled
                    style={{
                        width: "100%",
                    }}
                >
                    Subscription in progress…
                </Button>
            ) : (
                // No refresh button here: there's nothing yet to refresh, and this
                // state already revalidates on window focus (see the effect above).
                <form
                    action={checkoutUrl}
                    method="post"
                    target="_blank"
                    rel="noopener"
                    onSubmit={(event) => {
                        if (onBeforeCheckout?.(event)) {
                            return;
                        }
                        void trackUpgradeClicked({
                            surface: upgradeSurface,
                            conversionContext,
                            fallbackPlan: "free",
                        });
                        // Let the browser perform the native target=_blank form submission before
                        // this state change removes the form from the DOM. Updating synchronously
                        // can disconnect the form during the submit event, so no POST/new tab occurs.
                        window.setTimeout(() => updateCheckoutStarted(true), 0);
                    }}
                >
                    {/* rel must NOT include noreferrer: on a cross-origin form POST that opens a
                        new tab, it makes the browser send `Origin: null`, which the server's
                        exact-match CORS check rejects before the handler runs. noopener still
                        severs window.opener, which is the part that matters. */}
                    <Button
                        type="submit"
                        size="3"
                        style={{
                            width: "100%",
                        }}
                    >
                        <RocketIcon aria-hidden="true" />
                        Upgrade to Pro
                    </Button>
                </form>
            )}
        </Box>
    );
};

export default SubscriptionCard;
